import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { OverlayPanel } from "primereact/overlaypanel";
import { useRef, useState } from "react";
import InviceItems from "./new-invoice-items";

interface InvoiceDataProps {
  invoiceData: any[];
  orderData: any;
}

const InvoiceDataList: React.FC<InvoiceDataProps> = ({ invoiceData, orderData }) => {
  const op = useRef<any>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  const firstInvoiceNo = invoiceData.reduce((minNo: number, inv: any) => {
    return inv.invoiceNo < minNo ? inv.invoiceNo : minNo;
  }, Infinity);

  const invoiceGrossBeforeReward = (invoice: any) => {
    const isFirst = invoice.invoiceNo === firstInvoiceNo;
    const handling = isFirst ? (orderData?.handlingCharges || 0) : 0;
    const courier = isFirst ? (orderData?.courierCharges || 0) : 0;
    const packing = isFirst ? (orderData?.packingCharges || 0) : 0;
    const gateway = isFirst ? (invoice.invoicePaymentGatewayCharges || 0) : 0;
    // Deduct order-level discount (stored on the order, not the invoice-generation discount)
    // so Invoice Amount matches the "Total" shown in the Order Details section.
    const orderDiscount = isFirst ? (orderData?.discountAmount || 0) : 0;

    return Math.max(
      0,
      Math.round(
        (invoice.invoiceTaxableAmount || 0) +
        (invoice.invoiceTaxAmount || 0) +
        handling +
        courier +
        packing +
        gateway -
        orderDiscount
      )
    );
  };

  const taxAmountBodyTemplate = (invoice: any) => {
    return Helpers.formatAmountInINR(invoiceGrossBeforeReward(invoice));
  };

  const invoiceDateBodyTemplate = (invoice: any) => {
    const convertedDate = new Date(invoice.invoicedDate);
    const formattedDate = convertedDate.toLocaleDateString("en-gb", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    return formattedDate;
  };

  const rewardAmountBodyTemplate = (invoice: any) => {
    return Helpers.formatAmountInINR(invoice.invoiceRewardPointAmount || 0);
  };

  const paidAmountBodyTemplate = (invoice: any) => {
    const grossAmount = invoiceGrossBeforeReward(invoice);
    const rewardsAmount = invoice.invoiceRewardPointAmount || 0;
    const paidAmount = Math.max(0, grossAmount - rewardsAmount);
    return Helpers.formatAmountInINR(paidAmount);
  };

  const groupedInvoices = invoiceData.reduce((acc: any[], currentInvoice: any) => {
    const existingInvoiceIndex = acc.findIndex((invoice) => invoice.invoiceNo === currentInvoice.invoiceNo);

    if (existingInvoiceIndex === -1) {
      acc.push(currentInvoice);
    } else {
      const existingInvoice = acc[existingInvoiceIndex];
      const currentInvoiceDate = new Date(currentInvoice.invoicedDate);
      const existingInvoiceDate = new Date(existingInvoice.invoicedDate);

      if (currentInvoiceDate > existingInvoiceDate) {
        acc[existingInvoiceIndex] = currentInvoice;
      }
    }

    return acc;
  }, []);

  const showInvoiceItemDetails = (e: any, data: any) => {
    setSelectedInvoice(data);
    op.current.toggle(e);
  };

  return (
    <>
      <DataTable
        value={groupedInvoices}
        tableStyle={{ minWidth: "50rem" }}
        size="small"
        selectionMode="single"
        onSelectionChange={(e) => showInvoiceItemDetails(e.originalEvent, e.value)}
        dataKey="invoiceId"
        header="Invoice"
      >
        <Column field="invoiceNo" header="Invoice #" />
        <Column field="invoicedDate" header="Invoice Date" body={invoiceDateBodyTemplate} />
        <Column field="grossAmount" header="Invoice Amount" body={taxAmountBodyTemplate} />
        <Column field="rewardAmount" header="Reward Discount" body={rewardAmountBodyTemplate} />
        <Column field="PaidAmount" header="Paid Amount" body={paidAmountBodyTemplate} />
      </DataTable>

      <OverlayPanel ref={op} showCloseIcon>
        {selectedInvoice && <InviceItems selectedInvoice={selectedInvoice} orderinfo={orderData} />}
      </OverlayPanel>
    </>
  );
};

export default InvoiceDataList;
